import type { AdminUserListItem } from './api-client';
import { buildAdminUserActionSummary } from './dashboard-admin-users-state';
import { dashboardToneClasses } from './dashboard-presentation';
import { escapeHtml } from './html';

export function renderAdminUsersPagination(input: { total: number; page: number; perPage: number; totalPages?: number }): void {
  const total = document.getElementById('admin-users-total');
  if (total) total.textContent = input.total + ' users';
  const pagination = document.getElementById('admin-users-pagination');
  if (!pagination) return;
  const totalPages = Math.max(1, input.totalPages || Math.ceil(input.total / input.perPage));
  if (totalPages <= 1) {
    pagination.innerHTML = '';
    pagination.style.display = 'none';
    return;
  }
  const page = Math.min(Math.max(1, input.page), totalPages);
  pagination.style.display = '';
  pagination.innerHTML =
    '<button type="button" onclick="adminUsersPageChange(-1)" class="btn-ghost text-xs" ' + (page <= 1 ? ' disabled aria-disabled="true"' : '') + '><i class="fas fa-chevron-left mr-1"></i>Previous</button>' +
    '<span class="text-xs text-zinc-500">Page ' + page + ' of ' + totalPages + '</span>' +
    '<button type="button" onclick="adminUsersPageChange(1)" class="btn-ghost text-xs" ' + (page >= totalPages ? ' disabled aria-disabled="true"' : '') + '>Next<i class="fas fa-chevron-right ml-1"></i></button>';
}

export function renderAdminUsersLoadingState(): void {
  const total = document.getElementById('admin-users-total');
  if (total) total.textContent = 'loading...';
  const pagination = document.getElementById('admin-users-pagination');
  if (pagination) {
    pagination.innerHTML = '';
    pagination.style.display = 'none';
  }
  const container = document.getElementById('admin-users-list');
  if (!container) return;
  container.setAttribute('aria-busy', 'true');
  container.innerHTML = '<p class="text-center py-6 text-zinc-500 text-sm"><i class="fas fa-spinner fa-spin mr-2"></i>Loading users...</p>';
}

export function renderAdminUsersLoadError(message: string): void {
  const total = document.getElementById('admin-users-total');
  if (total) total.textContent = 'unavailable';
  const pagination = document.getElementById('admin-users-pagination');
  if (pagination) {
    pagination.innerHTML = '';
    pagination.style.display = 'none';
  }
  const container = document.getElementById('admin-users-list');
  if (!container) return;
  container.removeAttribute('aria-busy');
  container.innerHTML = '<div class="text-center py-6"><p class="text-red-300 text-sm">' + escapeHtml(message) +
    '</p><button type="button" onclick="adminUsersRetry()" class="btn-ghost text-xs mt-3"><i class="fas fa-rotate-right mr-1"></i>Retry</button></div>';
}

export function renderAdminUsersEmpty(message: string, canClear: boolean): string {
  const action = canClear
    ? '<button type="button" onclick="adminUsersClear()" class="btn-ghost text-xs mt-3"><i class="fas fa-eraser mr-1"></i>Clear current view</button>'
    : '';
  return '<div class="text-center py-6"><p class="text-zinc-500 text-sm">' + escapeHtml(message) + '</p>' + action + '</div>';
}

function optionSelected(value: string, selected: string): string {
  return value === selected ? 'selected' : '';
}

function escapeAttr(value: unknown): string {
  return escapeHtml(value);
}

export function renderAdminUsersItems(items: readonly AdminUserListItem[], currentUserId: number | null): string {
  return items.map((user) => {
    const isCurrentUser = currentUserId === user.id;
    const summary = buildAdminUserActionSummary(user);
    const roles = `<option value="user" ${optionSelected(user.role, 'user')}>user</option><option value="maintainer" ${optionSelected(user.role, 'maintainer')}>maintainer</option>`;
    const roleControl = isCurrentUser
      ? `<select disabled aria-label="Your current role" class="text-xs bg-zinc-900 border border-zinc-800 text-zinc-500 rounded px-2 py-1 cursor-not-allowed">${roles}</select>`
      : `<select onchange="adminSetRole(${user.id}, this.value, this)" data-current-role="${escapeAttr(user.role)}" data-username="${escapeAttr(user.username)}" class="text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 rounded px-2 py-1">${roles}</select>`;
    const suspendControl = isCurrentUser
      ? '<button type="button" disabled class="text-xs px-2 py-1 rounded border border-zinc-800 text-zinc-500 cursor-not-allowed">Current user</button>'
      : `<button onclick="adminToggleSuspend(${user.id}, ${!!user.suspended_at}, this)" data-username="${escapeAttr(user.username)}" class="text-xs px-2 py-1 rounded border ${user.suspended_at ? 'border-brand-700 text-brand-400 hover:bg-brand-900/30' : 'border-red-800 text-red-400 hover:bg-red-900/30'} transition-colors">${user.suspended_at ? 'Unsuspend' : 'Suspend'}</button>`;
    const summaryClasses = dashboardToneClasses(summary.tone, 'muted');
    return `<div class="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0 gap-3"><div class="min-w-0">
      <a href="/users/${encodeURIComponent(user.username)}" class="text-sm font-medium text-zinc-200 hover:text-brand-400 transition-colors">${escapeHtml(user.username)}</a>
      <span class="text-xs text-zinc-500 ml-2">${escapeHtml(user.email)}</span>${isCurrentUser ? '<span class="ml-2 text-xs text-brand-300 font-medium">you</span>' : ''}${user.suspended_at ? '<span class="ml-2 text-xs text-red-400 font-medium">suspended</span>' : ''}
      <div class="mt-1 flex flex-wrap gap-1.5 text-[11px]"><span class="px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-900 text-zinc-400">${escapeHtml(user.auth_provider || 'local')}</span>
      <span class="px-1.5 py-0.5 rounded border ${user.email_verified_at ? 'border-brand-800 text-brand-300 bg-brand-950/20' : 'border-amber-800 text-amber-300 bg-amber-950/20'}">${user.email_verified_at ? 'verified' : 'unverified'}</span>
      <span class="px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-900 text-zinc-400">${Number(user.active_access_token_count || 0)} tokens</span></div>
      <div class="mt-2 flex flex-wrap items-center gap-2 text-xs"><span class="px-2 py-1 rounded border ${summaryClasses}">${escapeHtml(summary.label)}</span><span class="text-zinc-500">${escapeHtml(summary.detail)}</span></div>
      </div><div class="flex items-center gap-2 flex-shrink-0">${roleControl}${suspendControl}</div></div>`;
  }).join('');
}
